import React, { useState, useEffect } from 'react';
import { Card, List, Input, Button, Empty, Tag, Modal, message, Popconfirm, Typography, Space, Divider } from 'antd';
import { 
  SearchOutlined, 
  DeleteOutlined, 
  ClearOutlined, 
  HistoryOutlined,
  QuestionCircleOutlined,
  MessageOutlined,
  ClockCircleOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { qaHistoryService, QARecord } from '../services/qaHistoryService';
import MarkdownViewer from './MarkdownViewer';
import ReactMarkdown from 'react-markdown';
import './QAHistory.css';
import '../styles/markdown.css';

const { Search } = Input;
const { Text, Title } = Typography;

const QAHistory: React.FC = () => {
  const [records, setRecords] = useState<QARecord[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<QARecord[]>([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedRecord, setSelectedRecord] = useState<QARecord | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  // 加载历史记录
  const loadRecords = () => {
    const allRecords = qaHistoryService.getAllRecords();
    // 按时间倒序排列
    allRecords.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    setRecords(allRecords);
    setFilteredRecords(allRecords);
  };

  useEffect(() => {
    loadRecords();
  }, []);

  // 搜索功能
  const handleSearch = (value: string) => {
    setSearchKeyword(value);
    if (value.trim()) {
      const searchResults = qaHistoryService.searchRecords(value);
      setFilteredRecords(searchResults);
    } else {
      setFilteredRecords(records);
    }
  };

  // 删除单条记录
  const handleDelete = (id: string) => {
    const success = qaHistoryService.deleteRecord(id);
    if (success) {
      message.success('删除成功');
      loadRecords();
    } else {
      message.error('删除失败');
    }
  };

  // 清空所有记录
  const handleClearAll = () => {
    qaHistoryService.clearAllRecords();
    message.success('已清空所有记录');
    setRecords([]);
    setFilteredRecords([]);
  };

  // 查看详情
  const showDetail = (record: QARecord) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  // 格式化时间
  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return '刚刚';
    if (minutes < 60) return `${minutes}分钟前`;
    if (hours < 24) return `${hours}小时前`;
    if (days < 7) return `${days}天前`;
    
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 按日期分组记录
  const groupedRecords = () => {
    const grouped = qaHistoryService.getRecordsByDate();
    const result: { date: string; records: QARecord[] }[] = [];
    
    grouped.forEach((records, date) => {
      result.push({ date, records });
    });
    
    return result;
  };

  return (
    <div className="qa-history-container">
      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <HistoryOutlined />
            <span>问答历史记录</span>
            <Tag color="blue" style={{ fontSize: '11px', padding: '1px 6px' }}>{records.length} 条记录</Tag>
          </div>
        }
        extra={
          <Popconfirm
            title="确定要清空所有历史记录吗？"
            onConfirm={handleClearAll}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              danger 
              icon={<ClearOutlined />}
              disabled={records.length === 0}
            >
              清空所有
            </Button>
          </Popconfirm>
        }
      >
        <Search
          placeholder="搜索问题或答案"
          allowClear
          enterButton={<SearchOutlined />}
          size="large"
          onSearch={handleSearch}
          style={{ marginBottom: 16 }}
        />

        {filteredRecords.length === 0 ? (
          <Empty description="暂无历史记录" />
        ) : (
          <List
            itemLayout="vertical"
            dataSource={filteredRecords}
            renderItem={(record) => (
              <List.Item
                key={record.id}
                className="qa-history-item"
                style={{ 
                  backgroundColor: '#fafafa', 
                  borderRadius: 8, 
                  padding: 16, 
                  marginBottom: 12,
                  border: '1px solid #f0f0f0',
                  transition: 'all 0.3s'
                }}
                actions={[
                  <Button 
                    type="primary"
                    ghost
                    icon={<EyeOutlined />}
                    onClick={() => showDetail(record)}
                    style={{ borderRadius: 6 }}
                  >
                    查看详情
                  </Button>,
                  <Popconfirm
                    title="确定要删除这条记录吗？"
                    onConfirm={() => handleDelete(record.id)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button 
                      danger 
                      icon={<DeleteOutlined />}
                      style={{ borderRadius: 6 }}
                    >
                      删除
                    </Button>
                  </Popconfirm>
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space align="center" style={{ marginBottom: 8 }}>
                      <QuestionCircleOutlined style={{ color: '#1890ff', fontSize: 18 }} />
                      <Text strong style={{ fontSize: 15, color: '#262626' }}>
                        {record.question}
                      </Text>
                    </Space>
                  }
                  description={
                    <div>
                      <Space align="center" style={{ marginBottom: 8 }}>
                        <ClockCircleOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {formatTime(record.timestamp)}
                        </Text>
                      </Space>
                      <Card 
                        size="small" 
                        style={{ 
                          backgroundColor: '#fff',
                          borderRadius: 6,
                          boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                        }}
                        bodyStyle={{ padding: 12 }}
                      >
                        <div style={{ 
                          maxHeight: '120px', 
                          overflow: 'hidden',
                          position: 'relative'
                        }}>
                          <MessageOutlined style={{ 
                            color: '#52c41a', 
                            marginRight: 8,
                            verticalAlign: 'top'
                          }} />
                          <span className="markdown-preview" style={{ 
                            display: 'inline-block',
                            width: 'calc(100% - 24px)',
                            lineHeight: 1.6
                          }}>
                            <ReactMarkdown
                              components={{
                                p: ({children}) => <span>{children}</span>,
                                code: ({children}) => (
                                  <code style={{
                                    padding: '2px 4px',
                                    margin: '0 2px',
                                    backgroundColor: '#f0f0f0',
                                    borderRadius: 3,
                                    fontSize: '0.9em',
                                    color: '#e01e5a'
                                  }}>
                                    {children}
                                  </code>
                                )
                              }}
                            >
                              {record.answer.length > 200 ? 
                                record.answer.substring(0, 200) + '...' : 
                                record.answer
                              }
                            </ReactMarkdown>
                          </span>
                          {record.answer.length > 200 && (
                            <div style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: 30,
                              background: 'linear-gradient(transparent, white)',
                              pointerEvents: 'none'
                            }} />
                          )}
                        </div>
                      </Card>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Card>

      {/* 详情弹窗 */}
      <Modal
        title={
          <Space>
            <HistoryOutlined />
            <span>问答详情</span>
          </Space>
        }
        visible={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={900}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}
      >
        {selectedRecord && (
          <div>
            {/* 时间信息 */}
            <Card 
              size="small" 
              style={{ marginBottom: 16, backgroundColor: '#fafafa' }}
              bodyStyle={{ padding: '12px 16px' }}
            >
              <Space>
                <ClockCircleOutlined style={{ color: '#8c8c8c' }} />
                <Text type="secondary">提问时间：</Text>
                <Text strong>{selectedRecord.timestamp.toLocaleString('zh-CN')}</Text>
              </Space>
            </Card>
            
            {/* 问题部分 */}
            <Card 
              title={
                <Space>
                  <QuestionCircleOutlined style={{ color: '#1890ff' }} />
                  <span>问题</span>
                </Space>
              }
              size="small" 
              style={{ marginBottom: 16 }}
              headStyle={{ backgroundColor: '#e6f7ff' }}
              bodyStyle={{ padding: 16 }}
            >
              <Title level={5} style={{ margin: 0, color: '#262626' }}>
                {selectedRecord.question}
              </Title>
            </Card>
            
            {/* 回答部分 */}
            <Card 
              title={
                <Space>
                  <MessageOutlined style={{ color: '#52c41a' }} />
                  <span>回答</span>
                </Space>
              }
              size="small"
              headStyle={{ backgroundColor: '#f6ffed' }}
              bodyStyle={{ padding: 0 }}
            >
              <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                <MarkdownViewer 
                  content={selectedRecord.answer}
                  fileName="answer.md"
                  showToc={false}
                />
              </div>
            </Card>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default QAHistory;